import { resolveDeclineCopy } from 'src/utils/resolveBillingError';
import { useEffect, useMemo, useState } from 'react';
import { Button, Modal } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useToastContext } from 'src/providers/ToastProvider/Toast.hook';
import { eAlertType } from 'src/providers/ToastProvider/Toast.enum';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';
import { LEAD_AGENT_PAYWALL_EVENTS } from 'src/hooks/analytics/leadAgentPaywallAnalytics.constants';
import { formatPrice } from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.functions';
import type { tLeadAgentSelectableTier } from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.types';
import {
  formatCapacityMultiple,
  projectCapacityBurn,
  projectCapacityPct,
  resolveCapacityMultiple,
} from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.functions';
import type { tCapacityBurnProjection } from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.functions';
import CheckoutCopyGate from '../CheckoutCopyGate.component';
import { toIntlLocale } from 'src/functions/date.functions';
import { formatPaymentDateShort } from '../../LeadAgentAcceptanceBanner.functions';
import {
  buildUpgradeRenewalBreakdown,
  formatLeadAgentRenewalPrice,
  parsePlanFrequency,
  renewalPeriodKey,
} from '../acceptanceModals.functions';
import { useUpgradeTierModal } from './UpgradeTierModal.hook';
import type { tUpgradeTierModalProps } from './UpgradeTierModal.types';

const NS = 'common/leadAgentUpgradeModal';

/**
 * Lead Agent self-serve tier-UPGRADE modal (C5 / RET-1304, RET-1614). OWNER-only
 * — the C8 usage banner only wires its CTA to open this for owners. Upgrading
 * charges IMMEDIATELY through the A8 tier-change endpoints (preview → change),
 * so this is a separate component from the deferred-PATCH ConfirmTierModal and
 * the reactivation modal, not a mode flag on either
 * (docs/solutions/patterns/reactivation-is-not-change-plan-per-intent-semantics.md).
 *
 * View machine (in the hook): confirm → processing → success | error | blocked.
 * The success copy says the charge is PROCESSING and the capacity updates once
 * the payment confirms (the Stripe webhook raises the cap via A2), so it never
 * claims the higher cap is live immediately.
 */
export default function UpgradeTierModal(props: tUpgradeTierModalProps) {
  const {
    workspaceId,
    currentTier,
    currentCapacityPct,
    capacityResetDate,
    onClose,
    onUpgraded,
  } = props;
  const { t, i18n, ready: copyReady } = useTranslation(NS);
  // The billing-error copy (decline map) lives in its own namespace (RET-1372);
  // resolveDeclineCopy requires a `t` bound to it (RET-1972).
  const { t: tBilling } = useTranslation('settings/SubscriptionManager');
  const { addAlert } = useToastContext();
  const { trackEvent } = useAnalytics();

  // Reuse the D8 `la_paywall_modal_opened` event with an `entry_point`
  // discriminator (the C2 acceptance modal passes `entry_point: 'banner'`), per
  // the "reuse existing events with a discriminator" convention — no new event
  // name minted. Fired once per open (the modal mounts fresh each time).
  useEffect(() => {
    trackEvent(LEAD_AGENT_PAYWALL_EVENTS.MODAL_OPENED, {
      entry_point: 'usage_banner',
      ...(currentTier ? { current_tier: currentTier.toLowerCase() } : {}),
    });
    // Mount-only: one emit per open. Deps intentionally empty.
  }, []);

  const {
    view,
    preview,
    projection,
    upgradeableTiers,
    suggestedTier,
    selectedTier,
    confirmDisabled,
    selectTier,
    confirm,
    retry,
  } = useUpgradeTierModal({
    workspaceId,
    currentTier,
    currentCapacityPct,
    capacityResetDate,
  });

  // Funnel outcome events (RET-1614): fire when the flow reaches a terminal
  // charge state. 'error' (retryable decline/3DS/transient) and 'blocked' (4xx
  // business rule) both count as a failed attempt; success carries the tier
  // actually charged. Keyed on view.status so each transition emits once.
  useEffect(() => {
    if (view.status === 'success') {
      trackEvent(LEAD_AGENT_PAYWALL_EVENTS.UPGRADE_SUCCEEDED, {
        entry_point: 'usage_banner',
        target_tier: view.result.targetTier.toLowerCase(),
      });
    } else if (view.status === 'error' || view.status === 'blocked') {
      trackEvent(LEAD_AGENT_PAYWALL_EVENTS.UPGRADE_FAILED, {
        entry_point: 'usage_banner',
        target_tier: selectedTier.toLowerCase(),
        outcome: view.status,
      });
    }
  }, [view.status]);

  // Only the suggested tier is visible; the rest live behind the toggle. Mirrors
  // ReactivateLeadAgentModal so the two pickers behave identically.
  const [showAllTiers, setShowAllTiers] = useState(false);

  const [acceptedTerms, setAcceptedTerms] = useState(false);
  // Inline terms-consent error: the submit stays enabled (see `confirmDisabled`
  // gates only preview/in-flight), and clicking it without accepting flips this
  // true to surface a red checkbox + helper line — not a toast, not a disabled
  // button (mirrors ReactivateLeadAgentModal / Fanny RET-1431).
  const [showTermsError, setShowTermsError] = useState(false);

  const isProcessing = view.status === 'processing';
  const hasUpgrade = upgradeableTiers.length > 0;

  const amountLabel = useMemo(() => {
    if (preview.status !== 'ready') return null;
    return formatPrice({
      price: preview.data.amountCents,
      currency: preview.data.currency,
    });
  }, [preview]);

  // Current tier for the "de X a Y" copy: prefer the preview's echo, fall back
  // to the prop the container passed (usageTier).
  const fromTier =
    preview.status === 'ready' && preview.data.currentTier
      ? preview.data.currentTier
      : currentTier;

  // ---- Capacity meter (RELATIVE to the current tier, never absolute) ----
  // A multiple anchored to STARTER ("9.2× more capacity" for Scale) is plainly
  // false for a Growth workspace moving to Scale — that is ~2.67× (1,200M/450M).
  // Every number the meter and the tier chips show is derived against `fromTier`.
  const capacityMultiple = useMemo(
    () => resolveCapacityMultiple(fromTier, selectedTier),
    [fromTier, selectedTier],
  );
  // The same consumption, re-expressed against the target tier's cap: the
  // customer arrives here from a banner that says "ya usaste el 92% de la
  // capacidad de tu plan", so the modal answers in that same unit (never tokens,
  // which no customer-facing surface exposes).
  const projectedCapacityPct = useMemo(
    () => projectCapacityPct(currentCapacityPct, capacityMultiple),
    [currentCapacityPct, capacityMultiple],
  );
  const showCapacityMeter =
    fromTier !== null &&
    currentCapacityPct !== null &&
    projectedCapacityPct !== null;

  // ---- Burn rate: what the CURRENT pace implies before the reset ----
  // The level alone ("you are at 92%") is not decidable: 92% on day 26 and 92% on
  // day 2 are different situations. This turns the level into a consequence — the
  // days the agent would spend mute — and says whether the target tier removes it.
  // Linear and burst-inclusive by decision: a spike is reported as the pace it is,
  // because suppressing it would silence the most urgent accounts. Null only when
  // the inputs make any projection impossible (no reset date, degenerate window).
  const currentBurn = useMemo(
    () =>
      projectCapacityBurn({
        capacityPct: currentCapacityPct,
        resetDate: capacityResetDate,
      }),
    [currentCapacityPct, capacityResetDate],
  );
  const targetBurn = useMemo(
    () =>
      projectCapacityBurn({
        capacityPct: currentCapacityPct,
        resetDate: capacityResetDate,
        capacityMultiple: capacityMultiple ?? 1,
      }),
    [currentCapacityPct, capacityResetDate, capacityMultiple],
  );
  /**
   * One short cell per row instead of a paragraph. The "this is a PROJECTION, not
   * a billing date" qualifier lives ONCE in the column header (`rateHeader`) — a
   * bare date next to the charge dates would read as another billing milestone and
   * earn us a complaint when it does not happen.
   */
  function rateLabelFor(burn: tCapacityBurnProjection | null): string | null {
    if (burn === null) return null;
    if (burn.status === 'exhausted') return t('capacity.rateExhausted');
    if (burn.status === 'lasts') return t('capacity.rateLasts');
    // Short form (no year): it never wraps the narrow column, and the year is
    // meaningless for a date that is at most one month away.
    const date = formatPaymentDateShort(burn.exhaustionDate);
    return date ? t('capacity.rateRunsOut', { date }) : null;
  }

  const currentRateLabel = rateLabelFor(currentBurn);
  const targetRateLabel = rateLabelFor(targetBurn);
  // The column (and its header) appear together, only when there is a projection.
  const showRateColumn = currentRateLabel !== null || targetRateLabel !== null;

  // ---- Renewal projection: INFORMATIONAL, fail-open ----
  // Nothing below may gate the charge. When the projection errors these are all
  // null and the breakdown + card price simply do not render.
  const readyProjection =
    projection.status === 'ready' ? projection.data : null;
  const breakdown = useMemo(
    () =>
      readyProjection ? buildUpgradeRenewalBreakdown(readyProjection) : null,
    [readyProjection],
  );
  const displayFrequency = parsePlanFrequency(readyProjection?.frequency);
  // Recurring price of the selected tier, derived from the projection's Lead
  // Agent line (RET-1417 Option A) instead of a third `getProducts` fetch — the
  // datum is already in the payload, with the period suffix from the same source.
  const tierPriceLabel =
    readyProjection && displayFrequency
      ? formatLeadAgentRenewalPrice(readyProjection, displayFrequency)
      : null;
  const periodKey = displayFrequency
    ? renewalPeriodKey(displayFrequency)
    : null;
  const periodLabel = periodKey ? t(`nextRenewal.period.${periodKey}`) : '';

  // Every guard that blocks a user action must surface feedback — a bare return
  // is an invisible dead button
  // (docs/solutions/patterns/reactivation-is-not-change-plan-per-intent-semantics.md).
  function handleClose() {
    if (isProcessing) {
      addAlert({ type: eAlertType.INFO, message: t('processing.wait') });
      return;
    }
    onClose();
  }

  function handleSelectTier(tier: tLeadAgentSelectableTier) {
    if (tier === selectedTier || isProcessing) return;
    setAcceptedTerms(false);
    setShowTermsError(false);
    selectTier(tier);
  }

  function handleConfirm() {
    if (confirmDisabled) return;
    // Terms consent is enforced here (button stays enabled): surface the inline
    // error instead of charging.
    if (!acceptedTerms) {
      setShowTermsError(true);
      return;
    }
    trackEvent(LEAD_AGENT_PAYWALL_EVENTS.UPGRADE_CONFIRMED, {
      entry_point: 'usage_banner',
      target_tier: selectedTier.toLowerCase(),
    });
    confirm();
  }

  function handleSuccessDismiss() {
    if (view.status === 'success') {
      onUpgraded?.({ tier: view.result.targetTier, result: view.result });
    }
    onClose();
  }

  const submitLoading = isProcessing;
  // The inline terms error shows ONLY while the checkbox is actionable, so it
  // never lingers beneath a disabled checkbox (e.g. after a preview retry).
  const showTermsErrorVisible = showTermsError && !confirmDisabled;

  // Sin la copy de este modal no hay nada que leer: `t()` devolvería
  // llaves crudas junto a montos correctos. Ver CheckoutCopyGate.
  if (!copyReady) {
    return (
      <CheckoutCopyGate
        className="LeadAgentUpgradeModal rounded-lg p-0 min-w-[34rem] max-w-[34rem]"
        onClose={handleClose}
      />
    );
  }

  return (
    <Modal.Legacy
      open
      onClickBackdrop={handleClose}
      className="LeadAgentUpgradeModal rounded-lg p-0 min-w-[34rem] max-w-[34rem]"
    >
      <div className="flex justify-between items-center p-4 border-b">
        <h3 className="text-lg font-semibold">{t('title')}</h3>
        <XMarkIcon
          onClick={handleClose}
          className="size-6 cursor-pointer text-neutral-500"
          data-testid="upgrade-modal-close"
        />
      </div>

      {/* ---- EMPTY (already on the top tier — defensive; the CTA gates this) ---- */}
      {!hasUpgrade ? (
        <div
          className="p-6 flex flex-col gap-4"
          data-testid="upgrade-modal-empty"
        >
          <p className="text-sm text-neutral-600">{t('empty.body')}</p>
          <Button
            color="ghost"
            size="sm"
            className="rounded self-end"
            onClick={onClose}
            data-testid="upgrade-modal-empty-dismiss"
          >
            {t('empty.dismiss')}
          </Button>
        </div>
      ) : null}

      {/* ---- SUCCESS ---- */}
      {hasUpgrade && view.status === 'success' ? (
        <div
          className="p-6 flex flex-col gap-4"
          data-testid="upgrade-modal-success"
        >
          <p className="text-base font-medium">{t('success.title')}</p>
          <p className="text-sm text-neutral-600">{t('success.processing')}</p>
          {view.result.invoiceId ? (
            <p
              className="text-xs text-neutral-500"
              data-testid="upgrade-modal-success-invoice"
            >
              {t('success.invoice', { invoiceId: view.result.invoiceId })}
            </p>
          ) : null}
          <Button
            color="primary"
            size="sm"
            className="rounded self-end"
            onClick={handleSuccessDismiss}
            data-testid="upgrade-modal-success-dismiss"
          >
            {t('success.dismiss')}
          </Button>
        </div>
      ) : null}

      {/* ---- BLOCKED (4xx business rule, NOT retryable) ---- */}
      {hasUpgrade && view.status === 'blocked' ? (
        <div
          className="p-6 flex flex-col gap-4"
          data-testid="upgrade-modal-blocked"
        >
          <p className="text-base font-medium text-error">
            {t('blocked.title')}
          </p>
          <p className="text-sm text-neutral-600">
            {view.message ?? t('blocked.body')}
          </p>
          {view.code ? (
            <p
              className="text-xs text-neutral-400"
              data-testid="upgrade-modal-blocked-code"
            >
              {view.code}
            </p>
          ) : null}
          <Button
            color="ghost"
            size="sm"
            className="rounded self-end"
            onClick={onClose}
            data-testid="upgrade-modal-blocked-dismiss"
          >
            {t('blocked.dismiss')}
          </Button>
        </div>
      ) : null}

      {/* ---- CONFIRM / PROCESSING / ERROR share the picker + summary ---- */}
      {hasUpgrade &&
      (view.status === 'confirm' ||
        view.status === 'processing' ||
        view.status === 'error') ? (
        <>
          <div className="p-4 flex flex-col gap-4">
            {/* Header row (label left, expander right) mirroring
                ReactivateLeadAgentModal, so the two pickers read the same. The
                label always renders — it names the card below; the expander only
                when there is more than one tier to offer, or it would be a dead
                control. */}
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium text-neutral-600">
                {t('confirm.yourNewCapacity')}
              </span>
              {upgradeableTiers.length > 1 ? (
                <button
                  type="button"
                  className="flex items-center gap-1 text-sm text-neutral-500 transition-colors hover:text-neutral-700"
                  onClick={() => setShowAllTiers((prev) => !prev)}
                  disabled={isProcessing}
                  data-testid="upgrade-modal-toggle"
                >
                  <ChevronDownIcon
                    className={classnames('size-4 transition-transform', {
                      'rotate-180': showAllTiers,
                    })}
                    aria-hidden="true"
                  />
                  {showAllTiers
                    ? t('confirm.hidePlans')
                    : t('confirm.showOtherPlans')}
                </button>
              ) : null}
            </div>

            <div
              className="flex flex-col gap-2"
              data-testid="upgrade-modal-plan-list"
            >
              {upgradeableTiers.map((tier) => {
                const isSelected = tier === selectedTier;
                // Collapsed by default to a single option: the one the burn rate
                // says actually solves the problem. The others are available, not
                // in the way.
                const isSuggested = tier === suggestedTier;
                if (!isSuggested && !showAllTiers) return null;
                // Per-tier multiple: each offered tier is a different jump from
                // the workspace's CURRENT tier, so the chip cannot be a fixed
                // per-tier string.
                const tierMultiple = resolveCapacityMultiple(fromTier, tier);
                return (
                  <label
                    key={tier}
                    className={classnames(
                      'flex items-center gap-3 rounded border p-3 cursor-pointer',
                      {
                        'border-primary bg-primary/5': isSelected,
                        'border-base-300': !isSelected,
                      },
                    )}
                    data-testid={`upgrade-tier-option-${tier}`}
                  >
                    <input
                      type="radio"
                      name="lead-agent-upgrade-tier"
                      className="radio radio-primary radio-sm"
                      checked={isSelected}
                      onChange={() => handleSelectTier(tier)}
                      disabled={isProcessing}
                      data-testid={`upgrade-tier-radio-${tier}`}
                    />
                    <span className="flex-1 flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">
                        {t(`tier.${tier}`)}
                      </span>
                      {tierMultiple ? (
                        <span
                          className="badge badge-sm badge-primary badge-outline h-auto whitespace-nowrap py-1"
                          data-testid={`upgrade-tier-multiple-${tier}`}
                        >
                          {t('confirm.capacityMultiple', {
                            multiple: formatCapacityMultiple(
                              tierMultiple,
                              toIntlLocale(i18n?.language ?? ''),
                            ),
                          })}
                        </span>
                      ) : null}
                    </span>
                    {/* The projection only carries the SELECTED tier's price. */}
                    {isSelected && tierPriceLabel ? (
                      <span
                        className="text-sm font-bold whitespace-nowrap"
                        data-testid={`upgrade-tier-price-${tier}`}
                      >
                        {tierPriceLabel}
                      </span>
                    ) : null}
                  </label>
                );
              })}
            </div>

            {/* ---- Capacity meter: same consumption, both capacities ----
                Sits BELOW the chosen plan on purpose: it is the justification for
                the recommendation, not a preamble to it. The customer reads what
                we suggest first, then the evidence for why. */}
            {showCapacityMeter ? (
              <div
                className="flex flex-col gap-2"
                data-testid="upgrade-modal-capacity-meter"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    {t('capacity.title')}
                  </p>
                  {showRateColumn ? (
                    <p
                      className="text-xs font-semibold uppercase tracking-wide text-neutral-500"
                      data-testid="upgrade-modal-rate-header"
                    >
                      {t('capacity.rateHeader')}
                    </p>
                  ) : null}
                </div>

                <div className="flex items-center gap-2">
                  <span className="flex items-center gap-2 basis-40 shrink-0 text-sm">
                    <span className="font-semibold">
                      {t(`tier.${fromTier}`)}
                    </span>
                    <span className="badge badge-xs badge-ghost h-auto whitespace-nowrap py-1">
                      {t('capacity.currentBadge')}
                    </span>
                  </span>
                  <span
                    className="flex-1 h-2 rounded-full bg-base-200 overflow-hidden"
                    role="progressbar"
                    aria-valuenow={currentCapacityPct ?? 0}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={t('capacity.currentAria', {
                      tier: t(`tier.${fromTier}`),
                    })}
                  >
                    <span
                      className="block h-full rounded-full bg-warning"
                      data-testid="upgrade-modal-capacity-fill-current"
                      // Dynamic fill width — the codebase's accepted exception to
                      // the no-inline-style rule (mirrors the C8 usage banner bar).
                      style={{
                        width: `${Math.min(currentCapacityPct ?? 0, 100)}%`,
                      }}
                    />
                  </span>
                  <span className="basis-10 shrink-0 text-right text-sm font-bold text-warning">
                    {t('capacity.pct', { pct: currentCapacityPct })}
                  </span>
                  {showRateColumn ? (
                    <span
                      className="basis-36 shrink-0 text-right text-xs leading-tight text-warning"
                      data-testid="upgrade-modal-rate-current"
                    >
                      {currentRateLabel}
                    </span>
                  ) : null}
                </div>

                <div className="flex items-center gap-2">
                  <span className="flex items-center gap-2 basis-40 shrink-0 text-sm">
                    <span className="font-semibold">
                      {t(`tier.${selectedTier}`)}
                    </span>
                    {selectedTier === suggestedTier ? (
                      <span className="badge badge-xs badge-primary h-auto whitespace-nowrap py-1">
                        {t('capacity.suggestedBadge')}
                      </span>
                    ) : null}
                  </span>
                  <span
                    className="flex-1 h-2 rounded-full bg-base-200 overflow-hidden"
                    role="progressbar"
                    aria-valuenow={projectedCapacityPct ?? 0}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={t('capacity.suggestedAria', {
                      tier: t(`tier.${selectedTier}`),
                    })}
                  >
                    <span
                      className="block h-full rounded-full bg-primary"
                      data-testid="upgrade-modal-capacity-fill-target"
                      // Dynamic fill width — see the note on the bar above.
                      style={{
                        width: `${Math.min(projectedCapacityPct ?? 0, 100)}%`,
                      }}
                    />
                  </span>
                  <span className="basis-10 shrink-0 text-right text-sm font-bold text-primary">
                    {t('capacity.pct', { pct: projectedCapacityPct })}
                  </span>
                  {showRateColumn ? (
                    <span
                      className="basis-36 shrink-0 text-right text-xs font-medium leading-tight text-primary"
                      data-testid="upgrade-modal-rate-target"
                    >
                      {targetRateLabel}
                    </span>
                  ) : null}
                </div>

                <p className="text-xs text-neutral-500">
                  {t('capacity.caption')}
                </p>
              </div>
            ) : null}

            {/* ---- Charge summary (A8 tier-change preview) ---- */}
            <div
              className="rounded border border-base-300 p-3 flex flex-col gap-3"
              data-testid="upgrade-modal-summary"
            >
              {preview.status === 'loading' ? (
                <p
                  className="text-sm text-neutral-500"
                  data-testid="upgrade-modal-preview-loading"
                >
                  {t('confirm.loadingPreview')}
                </p>
              ) : null}

              {preview.status === 'error' ? (
                <div
                  className="flex flex-col gap-2"
                  data-testid="upgrade-modal-preview-error"
                >
                  <p className="text-sm text-error">{t('errors.preview')}</p>
                  <button
                    type="button"
                    className="self-start text-sm text-primary underline"
                    onClick={retry}
                    data-testid="upgrade-modal-preview-retry"
                  >
                    {t('errors.retry')}
                  </button>
                </div>
              ) : null}

              {preview.status === 'ready' && amountLabel ? (
                <div className="flex justify-between text-sm">
                  <span className="flex flex-col">
                    <span>{t('confirm.chargeNow')}</span>
                    <span className="text-xs text-neutral-500">
                      {breakdown?.renewalDate
                        ? t('confirm.chargeNowHintDated', {
                            date: breakdown.renewalDate,
                            defaultValue: t('confirm.chargeNowHint'),
                          })
                        : t('confirm.chargeNowHint')}
                    </span>
                  </span>
                  <span
                    className="font-semibold"
                    data-testid="upgrade-modal-amount"
                  >
                    {amountLabel}
                  </span>
                </div>
              ) : null}
            </div>

            {/* ---- "Tu próxima renovación" (A6) ----
                Same shared breakdown the acceptance + reactivation modals show,
                so the three paying surfaces read identically. INFORMATIONAL: it
                renders only when the projection resolved; a loading/error state
                paints nothing and never blocks the charge. */}
            {breakdown ? (
              <div
                className="rounded border border-base-300 p-3 flex flex-col gap-1"
                data-testid="upgrade-modal-renewal"
              >
                <p className="text-sm font-medium">{t('nextRenewal.title')}</p>
                {breakdown.renewalDate ? (
                  <p
                    className="text-xs text-neutral-500"
                    data-testid="upgrade-modal-renewal-date"
                  >
                    {breakdown.renewalDate}
                  </p>
                ) : null}
                {breakdown.renewalLines.map((line, index) => (
                  <div
                    key={line.isNew ? 'lead-agent-line' : `plan-line-${index}`}
                    className="flex justify-between text-sm"
                  >
                    <span className="flex items-center gap-2">
                      {line.isNew
                        ? t('nextRenewal.leadAgentLine', {
                            tier: t(`tier.${selectedTier}`),
                          })
                        : line.description}
                      {/* "NUEVO" would be wrong here: the add-on is not new, it
                          is growing. Same component, honest label. */}
                      {line.isNew ? (
                        <span className="badge badge-xs badge-primary h-auto py-1">
                          {t('nextRenewal.upgraded')}
                        </span>
                      ) : null}
                    </span>
                    <span>{line.amount}</span>
                  </div>
                ))}
                <div className="flex justify-between text-sm font-bold border-t border-base-200 pt-2 mt-1">
                  <span>
                    {breakdown.renewalDate
                      ? t('nextRenewal.totalDated', {
                          period: periodLabel,
                          date: breakdown.renewalDate,
                        })
                      : t('nextRenewal.total', { period: periodLabel })}
                  </span>
                  <span data-testid="upgrade-modal-renewal-total">
                    {breakdown.renewalTotal}
                  </span>
                </div>
              </div>
            ) : null}

            {/* ---- Retryable error (decline / transient / unknown) ---- */}
            {view.status === 'error' ? (
              <div
                className="flex flex-col gap-1 rounded border border-error/30 bg-error/5 p-3"
                data-testid="upgrade-modal-error"
              >
                <p className="text-sm text-error">
                  {resolveDeclineCopy(view.message, tBilling) ??
                    t('errors.charge')}
                </p>
              </div>
            ) : null}

            <label
              className={classnames(
                'flex items-start gap-2 text-sm cursor-pointer',
                { 'text-error': showTermsErrorVisible },
              )}
            >
              <input
                type="checkbox"
                className={classnames('checkbox checkbox-sm mt-0.5', {
                  'checkbox-error': showTermsErrorVisible,
                })}
                checked={acceptedTerms}
                onChange={(event) => {
                  setAcceptedTerms(event.target.checked);
                  if (event.target.checked) setShowTermsError(false);
                }}
                disabled={confirmDisabled}
                data-testid="upgrade-modal-terms"
              />
              <span>{t('confirm.terms')}</span>
            </label>
            {showTermsErrorVisible ? (
              <p
                className="text-sm text-error"
                data-testid="upgrade-modal-terms-error"
              >
                {t('confirm.termsError')}
              </p>
            ) : null}
          </div>

          <div className="LeadAgentUpgradeModal__Actions p-4 bg-base-200 flex gap-3">
            <Button
              color="primary"
              size="sm"
              className="rounded flex-1"
              onClick={handleConfirm}
              disabled={confirmDisabled}
              loading={submitLoading}
              data-testid="upgrade-modal-submit"
            >
              {view.status === 'error'
                ? t('confirm.retrySubmit')
                : amountLabel
                  ? t('confirm.submitWithAmount', { amount: amountLabel })
                  : t('confirm.submit')}
            </Button>
            <Button
              color="ghost"
              size="sm"
              className="rounded"
              onClick={handleClose}
              disabled={isProcessing}
              data-testid="upgrade-modal-cancel"
            >
              {t('confirm.cancel')}
            </Button>
          </div>
        </>
      ) : null}
    </Modal.Legacy>
  );
}
